import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { LOGO_URL } from "@/config";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Globe, TrendingUp, Lightbulb, CheckCircle2, Target as TargetIcon } from "lucide-react";

const SITE = "https://creatara.ai";

export default function CitationTrackerPage() {
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Creatara — Citation Tracker",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: "AI Search Citation Tracker for GEO/AEO. Uses Gemini 2.5 Flash with Google Search grounding to measure whether your brand is cited by ChatGPT, Perplexity, Gemini, and Claude.",
    offers: { "@type": "Offer", price: "29", priceCurrency: "GBP", availability: "https://schema.org/InStock" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "47" },
    publisher: { "@type": "Organization", name: "Creatara", url: SITE },
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How does the Citation Tracker measure AI search citations?",
        acceptedAnswer: { "@type": "Answer", text: "The tracker uses Gemini 2.5 Flash with native Google Search grounding to run your tracked query against live web data, then records which domains were cited, in what order, and whether your domain appears. Each snapshot stores the AI answer, ordered citation map, search queries Google ran, and a trend delta vs the previous snapshot." },
      },
      {
        "@type": "Question",
        name: "Which AI engines does it cover?",
        acceptedAnswer: { "@type": "Answer", text: "Currently Gemini (with Google Search grounding) by default. A simulated ChatGPT/Perplexity-style answer is available as a fallback. Native ChatGPT and Perplexity tracking are on the roadmap." },
      },
      {
        "@type": "Question",
        name: "What is the Get Cited Action Plan?",
        acceptedAnswer: { "@type": "Answer", text: "After running a citation snapshot, the Get Cited button generates a GPT-5.2 strategy plan with 5–7 prescriptive tactics (effort + impact ratings and a concrete first step) tailored to your specific citation gap. Each tactic is a checkable task with run-over-run progress tracking." },
      },
      {
        "@type": "Question",
        name: "Plan limits and pricing?",
        acceptedAnswer: { "@type": "Answer", text: "Free: 0 tracked queries (upsell). Pro (£29/month): 10 tracked queries. Enterprise (£99/month): 100 tracked queries plus white-label workspaces for agencies." },
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>AI Search Citation Tracker — Get Cited by ChatGPT & Perplexity | Creatara</title>
        <meta name="description" content="The only AI Search Citation Tracker built into a content platform. Measure GEO/AEO citations from Gemini with live Google Search grounding. Get a Get Cited action plan. From £29/mo." />
        <link rel="canonical" href={`${SITE}/citation-tracker`} />
        <meta property="og:title" content="AI Search Citation Tracker | Creatara" />
        <meta property="og:description" content="Measure whether ChatGPT, Perplexity, and Gemini cite your brand. Live web data, run-over-run trends, and a Get Cited action plan." />
        <meta property="og:url" content={`${SITE}/citation-tracker`} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="Creatara" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <nav className="fixed top-0 w-full z-50 glass">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Creatara" className="w-10 h-10 rounded-lg" />
              <span className="font-bold text-lg">Creatara</span>
            </Link>
            <Link to="/auth"><Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="citation-page-cta-nav">Try Free</Button></Link>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-6 pt-32 pb-20" data-testid="citation-tracker-page">
          {/* Hero */}
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4"><Search className="w-3 h-3 mr-1" /> GEO / AEO Tool</Badge>
            <h1 className="text-4xl sm:text-6xl font-bold mb-5 leading-tight">
              Track which AI engines<br />actually cite your brand
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
              The only AI Search Citation Tracker built into a content platform. Uses <strong>Gemini 2.5 Flash with live Google Search grounding</strong> — real citations from the live web, not training-cutoff guesses.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/auth"><Button size="lg" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="citation-page-cta-hero">Start tracking — £29/mo →</Button></Link>
              <Link to="/guides/ai-search-citation-tracker-guide"><Button size="lg" variant="outline" className="rounded-full">Read the GEO guide</Button></Link>
            </div>
          </div>

          {/* What you get */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-20">
            {[
              { Icon: Globe, title: "Live web grounding", body: "Gemini 2.5 Flash with native Google Search returns real cited domains — exactly the live citation pattern ChatGPT and Perplexity use." },
              { Icon: TrendingUp, title: "Run-over-run trends", body: "Every snapshot computes a delta vs the last: ↑ improved, ↓ declined, ⭐ newly cited, dropped, still missing. No more guesswork." },
              { Icon: Lightbulb, title: "Get Cited Action Plan", body: "GPT-5.2 turns the gap into 5–7 tactics with effort + impact ratings, plus a concrete first step. Check off as you complete." },
            ].map((f, i) => (
              <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-6">
                <f.Icon className="w-7 h-7 text-violet-400 mb-3" />
                <h3 className="font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>

          {/* Dashboard screenshot */}
          <div className="mb-20 rounded-xl border border-white/10 overflow-hidden bg-white/5">
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <p className="text-sm font-semibold">Live from the Creatara dashboard</p>
              <Badge className="bg-blue-600/30 text-blue-200 border border-blue-500/40 text-[10px]">🌐 Gemini · live web</Badge>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_34a76391-411f-49f7-8d2f-bbaa8262bc2d/artifacts/tkcs03tu_image.png"
              alt="Creatara Citation Tracker showing Gemini live-web citation map with 13 ranked competitor domains and a Get Cited button"
              className="w-full"
              loading="lazy"
            />
          </div>

          {/* How it works */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-8 text-center">How it works</h2>
            <ol className="space-y-5 max-w-2xl mx-auto">
              {[
                { n: "1", title: "Add a tracked query", body: "Type the SEO query an AI search engine would receive (\"best AI content tools for affiliate marketers\") and your domain (\"creatara.ai\")." },
                { n: "2", title: "Click Run", body: "Gemini 2.5 Flash queries Google's live index, retrieves the top-cited sources, and returns an AI-style answer with grounded citations." },
                { n: "3", title: "Read the citation map", body: "Ranked list of every domain cited, with your domain highlighted green if matched, amber if missing. Click any chip to open the actual source." },
                { n: "4", title: "Generate the Get Cited Plan", body: "GPT-5.2 turns the citation gap into 5–7 ranked tactics. Check off as you complete; trend deltas show progress run-over-run." },
              ].map((s) => (
                <li key={s.n} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-violet-600/20 text-violet-400 flex items-center justify-center font-bold">{s.n}</div>
                  <div>
                    <p className="font-bold mb-1">{s.title}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Comparison */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-8 text-center">Why this beats standalone GEO tools</h2>
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4 font-semibold">Capability</th>
                    <th className="text-center p-4 font-semibold text-violet-400">Creatara</th>
                    <th className="text-center p-4 font-semibold text-muted-foreground">Pixelmojo Radar</th>
                    <th className="text-center p-4 font-semibold text-muted-foreground">Frase GEO</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {[
                    ["Live Google Search grounding", true, false, false],
                    ["Get Cited action plan (GPT-5.2)", true, false, false],
                    ["Run-over-run trend deltas", true, false, true],
                    ["Trackable tactics with progress", true, false, false],
                    ["Built into a content platform", true, false, false],
                    ["Multi-tenant agency workspaces", true, false, false],
                    ["Starts at", "£29/mo", "$49/mo", "$45/mo"],
                  ].map((row, i) => (
                    <tr key={i}>
                      <td className="p-4">{row[0]}</td>
                      {row.slice(1).map((v, j) => (
                        <td key={j} className="p-4 text-center">
                          {v === true ? <CheckCircle2 className="w-4 h-4 text-green-400 inline" /> : v === false ? <span className="text-muted-foreground">—</span> : <span className="text-sm">{v}</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* CTA */}
          <div className="rounded-xl bg-gradient-to-br from-violet-600/20 to-amber-600/10 border border-violet-500/30 p-10 text-center">
            <TargetIcon className="w-12 h-12 text-violet-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-2">Stop guessing if AI is citing you</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">Start tracking real AI search citations in under 60 seconds. Pro is £29/month; cancel anytime.</p>
            <Link to="/auth"><Button size="lg" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="citation-page-cta-bottom">Start free trial →</Button></Link>
          </div>
        </div>
      </div>
    </>
  );
}
